import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del } from "../../lib/request"

/**
 * Bulk delete posts.
 * DELETE /api/v1/posts/bulk — body: JSON array of numeric post ids, e.g. [2100, 2111]
 */
export const deletePostsBulk = createAsyncThunk(
  "deletePostsBulk/delete",
  async ({ postIds }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const raw = Array.isArray(postIds) ? postIds : []
      const numeric = raw
        .map((id) => (typeof id === "number" ? id : Number(id)))
        .filter((n) => n != null && !Number.isNaN(n))
      const unique = [...new Set(numeric)]

      if (unique.length === 0) {
        return { postIds: [] }
      }

      await del("posts/bulk", { body: JSON.stringify(unique) })

      return { postIds: unique }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete posts")
    }
  },
)

const initialState = {
  status: "idle",
  error: null,
  lastDeletedPostIds: null,
}

const deletePostsBulkSlice = createSlice({
  name: "deletePostsBulk",
  initialState,
  reducers: {
    clearDeletePostsBulkError: (state) => {
      state.error = null
    },
    resetDeletePostsBulkStatus: (state) => {
      state.status = "idle"
      state.error = null
      state.lastDeletedPostIds = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deletePostsBulk.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(deletePostsBulk.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.lastDeletedPostIds = action.payload.postIds
      })
      .addCase(deletePostsBulk.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to delete posts"
      })
  },
})

export const { clearDeletePostsBulkError, resetDeletePostsBulkStatus } = deletePostsBulkSlice.actions

export default deletePostsBulkSlice.reducer
